import { useContext, useEffect } from 'react';
import {
  useHistory,
  useLocation,
  useParams,
  useRouteMatch
} from 'react-router-dom';
import { generatePath } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  ROUTE_PERSONS_ENTITY,
  ROUTE_PERSONS_ENTITY_PERSON,
  ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION,
  ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS,
  ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS_TESTDATA_EDIT,
  ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS_TESTDATA,
  ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_REPORT_TESTDATA,
  ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_REPORT
} from 'routes/RouteList';
import { getAgeFromDobOnSession } from 'utils/date';
import messages from 'messages';
import { openNewWindow } from 'utils/browser';
import { getTestSessionURL } from 'utils/url';
import { StoreContext } from 'index';
import { useSessionContext } from 'contexts/SessionContext';
import BreadcrumbPath from 'containers/partials/misc/BreadcrumbPath';
import { useTestDataContext } from 'contexts/TestDataContext';
import Loader from 'components/loader/Loader';
import { useEntityContext } from 'containers/pages/persons/store/useEntityContext';
import PersonSessionTabs from 'containers/pages/persons/PersonSessionTabs';
import ContextMenuWrapper from 'components/menu/ContextMenuWrapper';
import slugify from 'slugify';
import ContextMenu from 'components/menu/ContextMenu';
import {
  Panel,
  PanelBody,
  PanelHeader,
  PanelSubtitle,
  PanelTitle,
  PanelTitleButton,
  PanelTitleButtonsGroup,
  PanelTitleWrapper
} from 'components/panel';

const PersonSession = () => {
  const {
    routing: { locale }
  } = useContext(StoreContext);

  const { entityId, groupId, personId, sessionId, testDataId } = useParams();
  const { replace } = useHistory();
  let location = useLocation();
  const intl = useIntl();
  const person = useEntityContext(s => s.person);
  const { session, sessionLoading } = useSessionContext();
  const {
    testData,
    fetchTestData,
    activeDate,
    unsetAllData,
    setNewActiveDate
  } = useTestDataContext();

  const active = useRouteMatch([
    ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_REPORT_TESTDATA,
    ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_REPORT,
    ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS_TESTDATA_EDIT,
    ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS_TESTDATA,
    ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS,
    ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION
  ]);

  const isSessionRootView = useRouteMatch({
    path: ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION,
    strict: true,
    exact: true
  });
  const isTestsView = useRouteMatch({
    path: [
      ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS,
      ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS_TESTDATA_EDIT,
      ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS_TESTDATA
    ],
    strict: true
  });
  const isReportView = useRouteMatch({
    path: ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_REPORT_TESTDATA,
    strict: true
  });

  useEffect(() => {
    if (session?.id !== sessionId) return;

    if (
      (!testDataId && session && (isReportView || isTestsView)) ||
      isSessionRootView
    ) {
      let testData;
      if (session.isRecurring) {
        if (!activeDate) {
          const today = new Date();
          setNewActiveDate(today);
          testData = session.getTestDataByDate(personId, today);
        } else {
          testData = session.getTestDataByDate(personId, activeDate);
        }
      } else {
        testData = session.getLastTestData(personId);
      }
      if (testData?.id) {
        const selectedPath =
          testData.finishedPercentage > 0 && !isTestsView
            ? ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_REPORT_TESTDATA
            : ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS_TESTDATA;

        replace(
          generatePath(selectedPath, {
            entityId,
            groupId,
            personId,
            sessionId,
            testDataId: testData.id
          })
        );
      } else {
        if (isSessionRootView) {
          replace(
            generatePath(ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS, {
              entityId,
              groupId,
              personId,
              sessionId
            })
          );
        } else {
          unsetAllData();
        }
      }
    } else if (testDataId && session && (isReportView || isTestsView)) {
      if (!testData.id || testData.id !== testDataId) {
        fetchTestData(session.id, testDataId);
      }
    }
  }, [testDataId, session, location]);

  const openTest = (entityId, sessionId, packageId) => {
    const url = getTestSessionURL({
      entityId,
      sessionId,
      packageType: packageId,
      locale,
      personId
    });
    openNewWindow(url);
  };

  return (
    <Panel active={active.isExact}>
      <PanelHeader>
        <BreadcrumbPath
          crumbs={[
            {
              path: generatePath(ROUTE_PERSONS_ENTITY, {
                entityId,
                groupId
              }),
              label: intl.formatMessage(messages.breadcrumbSporters)
            },
            {
              path: generatePath(ROUTE_PERSONS_ENTITY_PERSON, {
                entityId,
                groupId,
                personId
              }),
              label: intl.formatMessage(messages.breadcrumbSportersSessions, {
                firstname: person.firstname,
                lastname: person.lastname
              })
            },
            {
              path: generatePath(ROUTE_PERSONS_ENTITY, {
                entityId,
                groupId
              }),
              label: intl.formatMessage(messages.breadcrumbSporters)
            }
          ]}
        />
        <PanelTitleWrapper>
          <PanelTitle>{session.name}</PanelTitle>
          <PanelSubtitle>
            {session.startdate && person.birthdate && (
              <FormattedMessage
                {...messages.labelSessionSporterAgeAtTestDate}
                values={{
                  age: getAgeFromDobOnSession(
                    person.birthdate,
                    session.startdate
                  )
                }}
              />
            )}
          </PanelSubtitle>
        </PanelTitleWrapper>
        <PanelTitleButtonsGroup>
          <ContextMenuWrapper
            trigger={
              <PanelTitleButton
                dataQa={slugify(
                  `${person.firstname.toLowerCase()}_${person.lastname.toLowerCase()}_${session.name}`,
                  {
                    lower: true
                  }
                )}
              />
            }
          >
            <ContextMenu />
          </ContextMenuWrapper>
        </PanelTitleButtonsGroup>
      </PanelHeader>
      <PanelBody>
        {sessionLoading ? (
          <Loader />
        ) : (
          <>{!isSessionRootView && <PersonSessionTabs />}</>
        )}
      </PanelBody>
    </Panel>
  );
};

export default PersonSession;
